import axios from "axios";
import { baseURL } from "../config/index";
import {
  clearAuthenticationSession,
  getAuthenticationSession,
  getStoredAccountType,
  setAuthenticationSession,
} from "./authSession";

const ACCESS_TOKEN_REFRESH_BUFFER_MS = 60 * 1000;
let isRefreshing = false;
let failedQueue = [];

const AUTH_PATHS = Object.freeze({
  admin: {
    refresh: "/api/v1/admin/refreshToken",
    logout: "/api/v1/admin/logout",
    login: "/admin/login",
  },
  partner: {
    refresh: "/api/v1/partner/refreshToken",
    logout: "/api/v1/partner/logout",
    login: "/partner/login",
  },
});

const getAuthPaths = (accountType) => AUTH_PATHS[accountType] || null;

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  failedQueue = [];
};

const requestRefreshedAccessToken = async (refreshURL, allowRaceRetry = true) => {
  try {
    return await axios.post(
      refreshURL,
      {},
      { withCredentials: true },
    );
  } catch (error) {
    if (allowRaceRetry && error.response?.status === 409) {
      await new Promise((resolve) => window.setTimeout(resolve, 200));
      return requestRefreshedAccessToken(refreshURL, false);
    }
    throw error;
  }
};

const refreshAccessToken = async (accountType) => {
  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    });
  }

  const paths = getAuthPaths(accountType);
  if (!paths) throw new Error("Unsupported authentication account type");

  isRefreshing = true;

  try {
    const response = await requestRefreshedAccessToken(
      `${baseURL}${paths.refresh}`,
    );
    const data = response.data?.data;

    if (
      response.data?.error !== false ||
      !data?.token ||
      !data?.expiresIn ||
      !data?.id ||
      !data?.role
    ) {
      throw new Error(
        response.data?.message || "Invalid refresh token response",
      );
    }

    setAuthenticationSession({
      token: data.token,
      expiresIn: data.expiresIn,
      id: data.id,
      role: data.role,
      accountType,
    });
    processQueue(null, data.token);
    return data.token;
  } catch (error) {
    processQueue(error, null);
    throw error;
  } finally {
    isRefreshing = false;
  }
};

export const bootstrapAuthentication = async () => {
  const accountType = getStoredAccountType();
  if (!accountType) {
    clearAuthenticationSession();
    return false;
  }

  try {
    await refreshAccessToken(accountType);
    return true;
  } catch (error) {
    clearAuthenticationSession();
    return false;
  }
};

export const logoutAuthentication = async ({ redirect = true } = {}) => {
  const accountType = getStoredAccountType();
  const paths = getAuthPaths(accountType);

  try {
    if (paths) {
      await axios.post(
        `${baseURL}${paths.logout}`,
        {},
        { withCredentials: true },
      );
    }
  } catch (error) {
    // Local authentication state must still be cleared if logout is offline.
  } finally {
    clearAuthenticationSession();
  }

  if (redirect) {
    window.location.href = paths?.login || "/";
  }
};

const api = axios.create({
  baseURL,
  withCredentials: true,
});

api.interceptors.request.use(
  async (config) => {
    let session = getAuthenticationSession();
    const accountType = session.accountType || getStoredAccountType();

    if (!accountType) {
      throw new Error("Missing authentication session");
    }

    const expiry = Number(session.expiresIn);
    if (
      !session.token ||
      !Number.isFinite(expiry) ||
      expiry <= Date.now() + ACCESS_TOKEN_REFRESH_BUFFER_MS
    ) {
      await refreshAccessToken(accountType);
      session = getAuthenticationSession();
    }

    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${session.token}`;
    return config;
  },
  (error) => Promise.reject(error),
);

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && originalRequest?._retry !== true) {
      originalRequest._retry = true;

      try {
        const accountType =
          getAuthenticationSession().accountType || getStoredAccountType();
        const newToken = await refreshAccessToken(accountType);
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        await logoutAuthentication();
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  },
);

export default api;
